import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Layout,
  List,
  Modal,
  notification,
  Row,
  Skeleton,
  Table,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExportOutlined,
  FallOutlined,
  ReloadOutlined,
  StopOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { FormatDate } from "components";
import { useNavigate } from "react-router-dom";
import {
  getBreakageSummary,
  getRedemption,
  isBreakageMockMode,
  listBalanceLots,
  requestRedemption,
} from "services/breakage-service";
import { formatAmountMinor } from "views/breakage/breakage-money";
import { getCompany } from "services/company-service";
import {
  aggregateBreakageLots,
  agingBucketsFromSummary,
} from "views/breakage/breakage-dashboard-aggregates";
import BreakageLotDetailModal from "views/breakage/breakage-lot-detail-modal";
import BreakageKpiCard from "views/breakage/breakage-kpi-card";
import { useAnimatedAmount } from "views/breakage/use-animated-amount";
import {
  BREAKAGE_KPI_HELP,
  formatSaldoCount,
  labelOriginType,
} from "views/breakage/breakage-labels";
import {
  BreakageStatusLegend,
  BreakageStatusSignal,
} from "views/breakage/breakage-status-signal";
import {
  isRedemptionFinanciallySettled,
  isValidRedemptionRequest,
  resolveOwnerCompanyPixKey,
} from "views/breakage/breakage-adapters";
import { participantLegalName } from "views/breakage/breakage-participant-display";
import charts from "views/home/configCharts";
import { formatMoney } from "utils";
import { themeColors, themeLayout } from "theme";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(
  ChartDataLabels,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
);

const { Content } = Layout;
const { Text, Title } = Typography;

const REDEMPTION_TIP_DISMISSED_KEY =
  "breakage.dashboard.redemptionUnavailableTipDismissed";

const readRedemptionTipDismissed = () => {
  try {
    return window.localStorage.getItem(REDEMPTION_TIP_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
};

const moneyFromMinor = (amountMinor) =>
  formatAmountMinor({ amountMinor, currency: "BRL" });

const AGING_COLORS = [
  themeColors.errorColor,
  themeColors.lightColor,
  themeColors.blueColor,
  themeColors.warningColor,
  themeColors.highlightColor,
];

/**
 * Destino + breakageOperationId reais — sem placeholders.
 * Shape: { breakageOperationId, destinationAccountId, destinationHolderDocument }
 * Preencher quando US12 + UI de destino existirem.
 */
const resolveRedemptionRequest = () => null;

const DASHBOARD_LOTS_PAGE_SIZE = 100;
const DASHBOARD_LOTS_MAX_PAGES = 50;

/** Same GET /balance-lots contract; walks cursor pages for Owner-scoped aggregates. */
const listAllBalanceLotsForDashboard = async () => {
  const items = [];
  let cursor;
  let pages = 0;

  while (pages < DASHBOARD_LOTS_MAX_PAGES) {
    const result = await listBalanceLots({
      limit: DASHBOARD_LOTS_PAGE_SIZE,
      cursor,
    });
    if (!result?.ok) {
      return {
        ok: false,
        items,
        error: result,
        unavailable: result?.unavailable,
      };
    }
    items.push(...(result.data?.items || []));
    pages += 1;
    const page = result.data?.page;
    if (!page?.hasMore || !page?.nextCursor) {
      return { ok: true, items };
    }
    cursor = page.nextCursor;
  }

  return { ok: true, items, truncated: true };
};

const BreakageDashboardScreen = () => {
  const navigate = useNavigate();
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [lotsLoading, setLotsLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [lotsError, setLotsError] = useState(null);
  const [lots, setLots] = useState([]);
  const [detailLotId, setDetailLotId] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [redemptionPending, setRedemptionPending] = useState(false);
  const [redemptionSettled, setRedemptionSettled] = useState(false);
  const [pendingRedemptionId, setPendingRedemptionId] = useState(null);
  const [showRedemptionTip, setShowRedemptionTip] = useState(
    () => !readRedemptionTipDismissed()
  );

  const mockMode = isBreakageMockMode();
  const redemptionRequest = useMemo(() => resolveRedemptionRequest(), []);
  const canRequestRedemption = isValidRedemptionRequest(redemptionRequest || {});

  const load = useCallback(async () => {
    setSummaryLoading(true);
    setLotsLoading(true);
    setSummaryError(null);
    setLotsError(null);
    try {
      const [summaryResult, lotsResult] = await Promise.all([
        getBreakageSummary(),
        listAllBalanceLotsForDashboard(),
      ]);

      if (summaryResult?.ok && summaryResult.data) {
        setSummary(summaryResult.data);
      } else {
        setSummary(null);
        setSummaryError(
          summaryResult?.unavailable
            ? "API Breakage não configurada. Configure REACT_APP_BREAKAGE_API_URL ou ative o mock explícito."
            : summaryResult?.error ||
              "Não foi possível carregar o valor disponível para resgate."
        );
      }

      if (lotsResult?.ok) {
        setLots(lotsResult.items || []);
      } else {
        setLots(lotsResult?.items || []);
        setLotsError(
          lotsResult?.unavailable
            ? "API Breakage não configurada."
            : lotsResult?.error?.error ||
              "Não foi possível carregar a listagem de saldos."
        );
      }
    } finally {
      setSummaryLoading(false);
      setLotsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const aggregates = useMemo(() => aggregateBreakageLots(lots), [lots]);

  // Total financeiro canônico: SOMENTE do summary. Sem fallback por soma de página.
  const totalMinor =
    typeof summary?.totalBreakageAmount?.amountMinor === "number"
      ? summary.totalBreakageAmount.amountMinor
      : null;

  const { displayMinor, animateTo } = useAnimatedAmount(totalMinor ?? 0, {
    duration: 1600,
  });

  const eligibleFromSummary =
    summary?.breakdown?.eligibleRemainingAmount?.amountMinor;
  const expiredFromSummary = summary?.breakdown?.expiredAmount?.amountMinor;
  const withinFromSummary = summary?.breakdown?.withinWindowAmount?.amountMinor;
  const ineligibleFromSummary =
    summary?.breakdown?.ineligibleAmount?.amountMinor;
  const openCountFromSummary = summary?.breakdown?.openBalanceCount;

  const eligible = {
    count: aggregates.byEligibility.ELIGIBLE?.count || 0,
    amountMinor:
      typeof eligibleFromSummary === "number"
        ? eligibleFromSummary
        : aggregates.byEligibility.ELIGIBLE?.amountMinor || 0,
    fromSummary: typeof eligibleFromSummary === "number",
  };
  const ineligible = {
    count: aggregates.byEligibility.INELIGIBLE?.count || 0,
    amountMinor:
      typeof ineligibleFromSummary === "number"
        ? ineligibleFromSummary
        : aggregates.byEligibility.INELIGIBLE?.amountMinor || 0,
    fromSummary: typeof ineligibleFromSummary === "number",
  };
  const expired = {
    count: aggregates.byAging.EXPIRED?.count || 0,
    amountMinor:
      typeof expiredFromSummary === "number"
        ? expiredFromSummary
        : aggregates.byAging.EXPIRED?.amountMinor || 0,
    fromSummary: typeof expiredFromSummary === "number",
  };
  const within = {
    count: aggregates.byAging.WITHIN_WINDOW?.count || 0,
    amountMinor:
      typeof withinFromSummary === "number"
        ? withinFromSummary
        : aggregates.byAging.WITHIN_WINDOW?.amountMinor || 0,
    fromSummary: typeof withinFromSummary === "number",
  };
  const openLotsCount =
    typeof openCountFromSummary === "number"
      ? openCountFromSummary
      : aggregates.totalLots;
  const openLotsFromSummary = typeof openCountFromSummary === "number";

  const kpiLoading = summaryLoading || lotsLoading;
  const summaryUnavailable = !summaryLoading && totalMinor === null;

  const agingChart = useMemo(() => {
    const fromSummary = agingBucketsFromSummary(summary?.expirationBuckets);
    const buckets = (fromSummary || aggregates.agingBuckets || []).filter(
      (b) => b.amountMinor > 0 || b.count > 0
    );
    if (buckets.length === 0) {
      return null;
    }

    return charts.doughnut(
      {
        labels: buckets.map((b) => b.label),
        datasets: [
          {
            data: buckets.map((b) => b.amountMinor / 100),
            backgroundColor: buckets.map(
              (_, i) => AGING_COLORS[i % AGING_COLORS.length]
            ),
            datalabels: {
              color: buckets.map(
                (_, i) => AGING_COLORS[i % AGING_COLORS.length]
              ),
            },
          },
        ],
      },
      false,
      "right",
      (data) => {
        const bucket = buckets[data.dataIndex];
        const countPart =
          bucket?.count > 0
            ? ` · ${formatSaldoCount(bucket.count)}`
            : "";
        return `${data.label}: ${formatMoney(data.raw)}${countPart}`;
      }
    );
  }, [aggregates.agingBuckets, summary?.expirationBuckets]);

  const originBarChart = useMemo(() => {
    const mix = aggregates.originMix || [];
    if (mix.length === 0) {
      return null;
    }
    return charts.bar(
      {
        labels: mix.map((x) => labelOriginType(x.originType)),
        datasets: [
          {
            data: mix.map((x) => x.amountMinor / 100),
            backgroundColor: themeColors.mediumColor,
            borderRadius: 4,
          },
        ],
      },
      (data) => formatMoney(data.raw)
    );
  }, [aggregates.originMix]);

  const pollRedemptionUntilSettled = async (redemptionId) => {
    if (!redemptionId) {
      return false;
    }
    const result = await getRedemption(redemptionId);
    if (result?.ok && isRedemptionFinanciallySettled(result.data)) {
      setRedemptionPending(false);
      setRedemptionSettled(true);
      await animateTo(0, { ms: 1600 });
      notification.success({
        message: "Resgate concluído",
        description: "A liquidação financeira foi confirmada.",
      });
      return true;
    }
    return false;
  };

  const onRedemption = () => {
    if (
      redeeming ||
      redemptionPending ||
      redemptionSettled ||
      summaryUnavailable ||
      !totalMinor ||
      totalMinor <= 0
    ) {
      return;
    }

    if (!canRequestRedemption) {
      notification.warning({
        message: "Resgate ainda não disponível",
        description:
          "A solicitação exige breakageOperationId canônico e conta destino reais. Ainda falta conectar o identificador da operação (US12).",
      });
      return;
    }

    const idempotencyKey =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `redemption-${Date.now()}`;

    Modal.confirm({
      title: "Confirmar solicitação de resgate",
      content: `Deseja solicitar o resgate de ${moneyFromMinor(totalMinor)}? A aceitação da solicitação não liquida o valor imediatamente.`,
      okText: "Enviar solicitação",
      cancelText: "Cancelar",
      onOk: async () => {
        setRedeeming(true);
        try {
          // Resolve the authenticated Owner company at submission time; never trust a stale UI copy.
          const currentCompany = await getCompany();
          const destinationPixKey = resolveOwnerCompanyPixKey(currentCompany);
          if (!destinationPixKey) {
            notification.error({
              message: "Chave Pix indisponível",
              description: "A empresa autenticada não possui uma chave Pix válida cadastrada.",
            });
            return;
          }
          const result = await requestRedemption({ ...redemptionRequest, destinationPixKey }, {
            idempotencyKey,
          });

          if (result?.rejected) {
            notification.error({
              message: "Solicitação inválida",
              description: result.error,
            });
            return;
          }

          if (result?.accepted) {
            const redemptionId = result.data?.redemptionId || null;
            setPendingRedemptionId(redemptionId);
            setRedemptionPending(true);

            if (isRedemptionFinanciallySettled(result.data)) {
              setRedemptionPending(false);
              setRedemptionSettled(true);
              await animateTo(0, { ms: 1600 });
              notification.success({
                message: "Resgate concluído",
                description: "A liquidação financeira foi confirmada.",
              });
              return;
            }

            notification.info({
              message: "Solicitação registrada",
              description:
                result.status === 202
                  ? "Pedido aceito e pendente de processamento. O valor disponível não foi zerado."
                  : "Pedido aceito. Aguardando confirmação financeira antes de atualizar o saldo.",
            });

            if (redemptionId) {
              await pollRedemptionUntilSettled(redemptionId);
            }
            return;
          }

          notification.error({
            message: "Não foi possível solicitar o resgate",
            description:
              result?.error || "Tente novamente ou contate o suporte Aly+.",
          });
        } finally {
          setRedeeming(false);
        }
      },
    });
  };

  const redemptionButtonLabel = () => {
    if (redemptionSettled) {
      return "Resgate concluído";
    }
    if (redemptionPending) {
      return "Solicitação pendente";
    }
    if (!canRequestRedemption) {
      return "Resgate em breve";
    }
    return "Solicitar resgate";
  };

  const participantColumns = [
    {
      title: "#",
      key: "rank",
      width: 50,
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Nome",
      dataIndex: "legalName",
      key: "legalName",
      align: "center",
      ellipsis: true,
      render: (value, record) => (
        <span title={record.participantId || undefined}>
          {(value && String(value).trim()) || "—"}
        </span>
      ),
    },
    {
      title: "Saldos",
      dataIndex: "lots",
      key: "lots",
      align: "center",
      width: 80,
    },
    {
      title: "Saldo disponível",
      dataIndex: "amountMinor",
      key: "amountMinor",
      align: "center",
      render: (value) => moneyFromMinor(value),
    },
    {
      title: "Próx. expiração",
      dataIndex: "nextExpiresAt",
      key: "nextExpiresAt",
      align: "center",
      render: (value) =>
        value ? <FormatDate value={value} format="DD/MM/YYYY" /> : "—",
    },
  ];

  const recentColumns = [
    {
      title: "Creditado",
      dataIndex: "creditedAt",
      key: "creditedAt",
      align: "center",
      width: 110,
      render: (value) => <FormatDate value={value} format="DD/MM/YYYY" />,
    },
    {
      title: "Nome",
      key: "legalName",
      align: "center",
      width: 140,
      ellipsis: true,
      render: (_, record) => {
        const name = participantLegalName(record);
        return (
          <span title={record.participantId || undefined}>
            {(name && String(name).trim()) || "—"}
          </span>
        );
      },
    },
    {
      title: "Valor",
      dataIndex: "remainingAmount",
      key: "remainingAmount",
      align: "center",
      width: 110,
      render: (value) => formatAmountMinor(value),
    },
    {
      title: "Situação",
      key: "status",
      align: "center",
      width: 72,
      render: (_, record) => (
        <BreakageStatusSignal
          eligibility={record.eligibility}
          agingCondition={record.agingCondition}
        />
      ),
    },
    {
      title: "Ações",
      key: "actions",
      align: "center",
      width: 120,
      render: (_, record) => (
        <Button type="link" onClick={() => setDetailLotId(record.balanceLotId)}>
          Ver detalhes
        </Button>
      ),
    },
  ];

  return (
    <Layout className="alymente-layout">
      <Layout.Header>
        <Row style={{ width: "100%" }} align="middle" justify="space-between">
          <Col>
            <Title level={4} style={{ marginBottom: 0, marginTop: 0 }}>
              Resgate de saldos
            </Title>
            <Text type="secondary">
              {summary?.asOf
                ? `Valores não utilizados · referência ${new Date(summary.asOf).toLocaleString("pt-BR")}`
                : "Valores não utilizados pelos participantes que podem ser resgatados pela empresa"}
            </Text>
          </Col>
          <Col style={{ textAlign: "right" }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={() => navigate("/breakage/redemptions")}
            >
              Consultar resgates
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={load}
              loading={summaryLoading || lotsLoading}
            >
              Atualizar
            </Button>
            {mockMode && (
              <Text type="warning" style={{ marginLeft: 12 }}>
                Mock ativo
              </Text>
            )}
          </Col>
        </Row>
      </Layout.Header>

      <Content style={{ marginBottom: 16 }}>
        {(summaryError || lotsError) && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="Dados Breakage indisponíveis"
            description={[summaryError, lotsError].filter(Boolean).join(" ")}
            action={
              <Button size="small" onClick={load}>
                Tentar novamente
              </Button>
            }
          />
        )}

        {redemptionPending && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Solicitação de resgate pendente"
            description={
              pendingRedemptionId
                ? `Pedido ${pendingRedemptionId} aceito. O saldo disponível só será atualizado após confirmação financeira.`
                : "Pedido aceito. O saldo disponível só será atualizado após confirmação financeira."
            }
            action={
              pendingRedemptionId ? (
                <Button
                  size="small"
                  onClick={() => pollRedemptionUntilSettled(pendingRedemptionId)}
                >
                  Atualizar status
                </Button>
              ) : null
            }
          />
        )}

        {showRedemptionTip &&
          !canRequestRedemption &&
          !summaryUnavailable && (
          <Alert
            type="warning"
            showIcon
            closable
            style={{ marginBottom: 16 }}
            message="Não há operação de resgate disponível"
            description="No momento não é possível solicitar o resgate por este painel. Assim que a operação estiver disponível, o botão será liberado."
            onClose={() => {
              setShowRedemptionTip(false);
              try {
                window.localStorage.setItem(REDEMPTION_TIP_DISMISSED_KEY, "1");
              } catch {
                // ignore quota / private mode
              }
            }}
          />
        )}

        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} xl={8} xxl={4}>
            <BreakageKpiCard
              loading={summaryLoading}
              title="Disponível para resgate"
              value={
                summaryUnavailable
                  ? "—"
                  : moneyFromMinor(displayMinor)
              }
              meta={
                summaryUnavailable
                  ? "Aguardando summary canônico"
                  : undefined
              }
              count={
                summaryUnavailable || redemptionSettled
                  ? undefined
                  : aggregates.totalLots
              }
              accent={themeColors.mediumColor}
              icon={<WalletOutlined />}
              help={BREAKAGE_KPI_HELP.available}
              showSparkline={false}
              footer={
                <Button
                  type="primary"
                  block
                  loading={redeeming}
                  disabled={
                    summaryLoading ||
                    summaryUnavailable ||
                    redeeming ||
                    redemptionPending ||
                    redemptionSettled ||
                    !canRequestRedemption ||
                    !totalMinor ||
                    totalMinor <= 0
                  }
                  onClick={onRedemption}
                >
                  {redemptionButtonLabel()}
                </Button>
              }
            />
          </Col>
          <Col xs={24} sm={12} xl={8} xxl={4}>
            <BreakageKpiCard
              loading={kpiLoading}
              title="Elegível para Breakage"
              value={moneyFromMinor(eligible.amountMinor)}
              count={eligible.fromSummary ? undefined : eligible.count}
              meta={
                eligible.fromSummary
                  ? "Do summary"
                  : "Prévia da listagem"
              }
              accent={themeColors.lightColor}
              icon={<CheckCircleOutlined />}
              help={BREAKAGE_KPI_HELP.eligible}
              sparkline={aggregates.sparklines.eligible}
            />
          </Col>
          <Col xs={24} sm={12} xl={8} xxl={4}>
            <BreakageKpiCard
              loading={kpiLoading}
              title="Vencido"
              value={moneyFromMinor(expired.amountMinor)}
              count={expired.fromSummary ? undefined : expired.count}
              meta={expired.fromSummary ? "Do summary" : "Prévia da listagem"}
              accent={themeColors.warningColor}
              icon={<FallOutlined />}
              help={BREAKAGE_KPI_HELP.expired}
              sparkline={aggregates.sparklines.expired}
            />
          </Col>
          <Col xs={24} sm={12} xl={8} xxl={4}>
            <BreakageKpiCard
              loading={kpiLoading}
              title="A vencer"
              value={moneyFromMinor(within.amountMinor)}
              count={within.fromSummary ? undefined : within.count}
              meta={within.fromSummary ? "Do summary" : "Prévia da listagem"}
              accent={themeColors.blueColor}
              icon={<ClockCircleOutlined />}
              help={BREAKAGE_KPI_HELP.within}
              sparkline={aggregates.sparklines.within}
            />
          </Col>
          <Col xs={24} sm={12} xl={8} xxl={4}>
            <BreakageKpiCard
              loading={kpiLoading}
              title="Não elegível"
              value={moneyFromMinor(ineligible.amountMinor)}
              count={ineligible.fromSummary ? undefined : ineligible.count}
              meta={
                ineligible.fromSummary ? "Do summary" : "Prévia da listagem"
              }
              accent={themeColors.errorColor}
              icon={<StopOutlined />}
              help={BREAKAGE_KPI_HELP.ineligible}
              sparkline={aggregates.sparklines.ineligible}
            />
          </Col>
          <Col xs={24} sm={12} xl={8} xxl={4}>
            <BreakageKpiCard
              loading={kpiLoading}
              title="Saldos em aberto"
              value={String(openLotsCount)}
              count={openLotsCount}
              meta={openLotsFromSummary ? "Do summary" : "Nesta listagem"}
              accent={themeColors.highlightColor}
              icon={<ExportOutlined />}
              help={BREAKAGE_KPI_HELP.openLots}
              sparkline={aggregates.sparklines.lotsCount}
            />
          </Col>
        </Row>

        <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <Card
              title="Por prazo de vencimento"
              bordered={false}
              style={{
                borderRadius: themeLayout.borderRadius,
                boxShadow: themeLayout.boxShadow,
              }}
              bodyStyle={{ height: 340 }}
            >
              {summaryLoading || lotsLoading ? (
                <Skeleton active paragraph={{ rows: 8 }} title={false} />
              ) : agingChart ? (
                <Doughnut {...agingChart} />
              ) : null}
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              title="De onde veio o saldo"
              bordered={false}
              style={{
                borderRadius: themeLayout.borderRadius,
                boxShadow: themeLayout.boxShadow,
              }}
              bodyStyle={{ height: 340 }}
            >
              {lotsLoading ? (
                <Skeleton active paragraph={{ rows: 8 }} title={false} />
              ) : originBarChart ? (
                <Bar {...originBarChart} />
              ) : (
                <List locale={{ emptyText: "Sem dados" }} dataSource={[]} />
              )}
            </Card>
          </Col>
        </Row>

        <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
          <Col xs={24} xl={12} style={{ minWidth: 0 }}>
            <Card
              title="Maiores saldos por participante"
              bordered={false}
              style={{
                height: "100%",
                overflow: "hidden",
                borderRadius: themeLayout.borderRadius,
                boxShadow: themeLayout.boxShadow,
              }}
              bodyStyle={{ overflow: "auto" }}
            >
              {lotsLoading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
              ) : (
                <Table
                  rowKey="participantId"
                  columns={participantColumns}
                  dataSource={aggregates.topParticipants}
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                />
              )}
            </Card>
          </Col>
          <Col xs={24} xl={12} style={{ minWidth: 0 }}>
            <Card
              title="Últimos saldos"
              bordered={false}
              style={{
                height: "100%",
                overflow: "hidden",
                borderRadius: themeLayout.borderRadius,
                boxShadow: themeLayout.boxShadow,
              }}
              bodyStyle={{ overflowX: "auto", paddingBottom: 8 }}
              extra={
                !lotsLoading && (
                  <Button
                    type="link"
                    onClick={() => navigate("/breakage/statement")}
                  >
                    Consultar saldos →
                  </Button>
                )
              }
            >
              {lotsLoading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
              ) : (
                <>
                  <Table
                    rowKey="balanceLotId"
                    columns={recentColumns}
                    dataSource={aggregates.recentLots}
                    pagination={false}
                    size="small"
                    scroll={{ x: 620 }}
                    tableLayout="fixed"
                  />
                  <BreakageStatusLegend />
                </>
              )}
            </Card>
          </Col>
        </Row>

        <Row justify="end" style={{ marginTop: 12}}>
          <Title level={5} type="secondary" style={{ margin: 0 }}>
            {formatSaldoCount(aggregates.totalLots)} na listagem carregada
            {lots.length >= 50
              ? " (prévia — indicadores abaixo do card principal não são total global)"
              : ""}
          </Title>
        </Row>
      </Content>

      <BreakageLotDetailModal
        open={Boolean(detailLotId)}
        balanceLotId={detailLotId}
        onClose={() => setDetailLotId(null)}
      />
    </Layout>
  );
};

export default BreakageDashboardScreen;
